import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSector } from '@/contexts/SectorContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { DropletIcon, Home, Tractor, ArrowRight, SunIcon, CloudRainIcon, BarChart3Icon, HomeIcon, ShowerHeadIcon, UmbrellaIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import useTranslation from '@/hooks/use-translation';

const SectorSelection = () => {
  const { setSector } = useSector();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isAuthenticated, loading } = useAuth();
  const { translate } = useTranslation();
  
  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/login');
    }
  }, [loading, isAuthenticated, navigate]);

  if (loading) {
    return (
      <div className="h-screen w-full flex items-center justify-center">
        <DropletIcon className="h-8 w-8 animate-pulse text-primary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }
  
  const selectSector = async (sector: 'agriculture' | 'household') => {
    setSector(sector);
    
    // Save to localStorage for guest mode and session persistence
    localStorage.setItem('aquagenius-sector', sector);
    
    toast({
      title: translate(`${sector === 'agriculture' ? 'Agriculture' : 'Household'} Mode Selected`),
      description: translate("You can change this anytime from your settings."),
    });
    
    navigate('/');
  };
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="max-w-4xl w-full">
        <div className="text-center mb-8">
          <DropletIcon className="h-12 w-12 mx-auto mb-4 text-primary" />
          <h1 className="text-3xl font-bold mb-2">
            {translate("Welcome to AquaGenius")}
          </h1>
          <p className="text-muted-foreground">
            {translate("Please select your primary water usage sector to get started.")}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card 
            className={cn(
              "relative overflow-hidden transition-all hover:border-primary cursor-pointer",
              "group"
            )}
            onClick={() => selectSector('household')}
          >
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <HomeIcon className="h-5 w-5" />
                {translate("Household")}
              </CardTitle>
              <CardDescription>
                {translate("For residential water management and conservation.")}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2">
                  <ShowerHeadIcon className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">{translate("Indoor Usage")}</span>
                </div>
                <div className="flex items-center gap-2">
                  <UmbrellaIcon className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">{translate("Outdoor Usage")}</span>
                </div>
              </div>
            </CardContent>
            <CardFooter>
              <Button className="w-full group-hover:bg-primary group-hover:text-primary-foreground">
                {translate("Select Household")}
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </CardFooter>
          </Card>

          <Card 
            className={cn(
              "relative overflow-hidden transition-all hover:border-primary cursor-pointer",
              "group"
            )}
            onClick={() => selectSector('agriculture')}
          >
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Tractor className="h-5 w-5" />
                {translate("Agriculture")}
              </CardTitle>
              <CardDescription>
                {translate("For farm and agricultural water optimization.")}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2">
                  <SunIcon className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">{translate("Irrigation")}</span>
                </div>
                <div className="flex items-center gap-2">
                  <CloudRainIcon className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">{translate("Rainfall")}</span>
                </div>
              </div>
            </CardContent>
            <CardFooter>
              <Button className="w-full group-hover:bg-primary group-hover:text-primary-foreground">
                {translate("Select Agriculture")}
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </CardFooter>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default SectorSelection;
